/**
 * dsh-work — RemotePanel：工作台「远程工作区」功能标签（双栏布局）。
 *
 * 左栏 = 档案列表（点击整行载入编辑；当前会话命中的档案高亮并自动载入）；
 * 右栏 = 表单（新增 / 编辑）。管理 dsh-remote 插件的档案——特性探测消费
 * /dsh-remote/* 路由，插件缺席时显示安装提示。
 *
 * 交互流（v0.3）：可从 ~/.ssh/config 导入连接信息 → 填/选登录方式 →
 * 「浏览」远端目录选项目根（名称留空时自动取目录名）→ 测试连接 → 保存
 * （保存即自动注册侧栏工作区，标题 `⇄ 名称 · user@host`）→ 删除（连带
 * 删侧栏工作区）。
 */
import React from 'react'
import { LocalFileBrowser, RemoteDirBrowser } from './remote-browser.js'
import { remoteIcon } from './icons.js'

const h = React.createElement
const { useCallback, useEffect, useState } = React

/** /dsh-remote/* 的统一请求封装（POST 一律 JSON，穿 415 围栏）。 */
async function callJson(path, body) {
	const isPost = body !== undefined
	const response = await fetch(path, {
		method: isPost ? 'POST' : 'GET',
		headers: { accept: 'application/json', ...(isPost ? { 'content-type': 'application/json' } : {}) },
		body: isPost ? JSON.stringify(body) : undefined,
	})
	let payload
	try { payload = await response.json() } catch { payload = undefined }
	if (!response.ok || payload === undefined) throw new Error(`请求失败（HTTP ${String(response.status)}）`)
	return payload
}

/** POSIX basename。 */
function baseNameOf(path) {
	const trimmed = path.replace(/\/+$/, '')
	const seg = trimmed.split('/').filter((s) => s !== '')
	return seg.length === 0 ? '' : seg[seg.length - 1]
}

/** 表单草稿（authMode 是三选一的 UI 形态，保存时折叠成 authMethod 两态）。 */
const EMPTY_FORM = { name: '', host: '', port: '22', username: '', authMode: 'key-file', identityFile: '', password: '', remotePath: '' }

function formOfProfile(profile) {
	return {
		name: profile.name,
		host: profile.host,
		port: String(profile.port),
		username: profile.username,
		authMode: profile.authMethod === 'password' ? 'password' : profile.identityFile !== '' ? 'key-file' : 'agent',
		identityFile: profile.identityFile,
		password: '',
		remotePath: profile.remotePath,
	}
}

/** 表单 → /save 与 /test 的 profile 载荷 + 一次性密码。 */
function payloadOfForm(form) {
	return {
		profileBody: {
			name: form.name,
			host: form.host,
			port: form.port,
			username: form.username,
			authMethod: form.authMode === 'password' ? 'password' : 'key',
			identityFile: form.authMode === 'key-file' ? form.identityFile : '',
			remotePath: form.remotePath,
		},
		password: form.authMode === 'password' && form.password !== '' ? form.password : undefined,
	}
}

/** 登录方式标签。 */
const AUTH_LABEL = { 'key-file': '密钥文件', agent: 'ssh-agent', password: '密码' }

/** 字段行：标签在上、控件在下（窄面板下不溢出的堆叠布局）。 */
function Field({ label, children }) {
	return h('div', { className: 'dwb-remote-field' },
		h('span', { className: 'dwb-remote-label' }, label),
		children,
	)
}

/** 已保存档案行（点击整行 = 载入编辑）。 */
function ProfileRow({ profile, active, busy, onEdit, onRemove }) {
	return h('div', {
		className: 'dwb-remote-item' + (active === true ? ' dwb-remote-itemactive' : ''),
		onClick: onEdit,
		title: `${profile.username}@${profile.host}:${String(profile.port)} · ${profile.remotePath}`,
	},
		h('span', { className: 'dwb-remote-dot' + (profile.connected === true ? ' dwb-remote-doton' : '') }),
		h('div', { className: 'dwb-remote-itemmain' },
			h('div', { className: 'dwb-remote-itemname' }, profile.name),
			h('div', { className: 'dwb-remote-itemsub' },
				`${profile.username}@${profile.host}:${String(profile.port)} · ${AUTH_LABEL[profile.authMethod === 'password' ? 'password' : profile.identityFile !== '' ? 'key-file' : 'agent']}`
				+ (profile.connected === true ? ' · 在线' : '')),
			h('div', { className: 'dwb-remote-itemsub dwb-remote-itempath' }, profile.remotePath),
		),
		h('div', { className: 'dwb-remote-itemops' },
			h('button', {
				type: 'button', className: 'dwb-remote-mini', disabled: busy,
				onClick: (event) => { event.stopPropagation(); onRemove() },
			}, '删除'),
		),
	)
}

/**
 * 远程工作区面板（单实例标签，双栏）。
 * props.visible：标签是否激活；props.cwd：当前会话 cwd（联动载入编辑）。
 */
export function RemotePanel({ visible, cwd }) {
	const [available, setAvailable] = useState(null) // null = 探测中
	const [profiles, setProfiles] = useState([])
	const [sshEntries, setSshEntries] = useState([]) // ~/.ssh/config 条目（空 = 隐藏导入下拉）
	const [form, setForm] = useState(EMPTY_FORM)
	const [nameDirty, setNameDirty] = useState(false) // 用户手填过名称后不再自动覆盖
	const [editingId, setEditingId] = useState('') // 当前编辑/联动载入的档案 id（高亮）
	const [manualMode, setManualMode] = useState(false) // 「＋新增」后脱离 cwd 联动抢占，直到保存或再点编辑
	const [formTouched, setFormTouched] = useState(false) // 用户改过任一字段后，自动联动不再覆盖表单
	const [busy, setBusy] = useState('')
	const [message, setMessage] = useState(null) // {ok, text}
	const [sshSelected, setSshSelected] = useState('') // 导入下拉受控值：选中后保持显示别名
	const [localPickerOpen, setLocalPickerOpen] = useState(false)
	const [remotePickerOpen, setRemotePickerOpen] = useState(false)

	const reload = useCallback(() => {
		callJson('/dsh-remote/list')
			.then((payload) => {
				setAvailable(true)
				if (payload.ok === true) setProfiles(payload.profiles)
			})
			.catch(() => setAvailable(false))
	}, [])

	// ssh-config 导入源（失败/为空静默隐藏）。
	useEffect(() => {
		callJson('/dsh-remote/ssh-config')
			.then((payload) => {
				if (payload.ok === true && Array.isArray(payload.entries) && payload.entries.length > 0) setSshEntries(payload.entries)
			})
			.catch(() => {})
	}, [])

	useEffect(() => { if (visible === true) reload() }, [reload, visible])

	// cwd 联动：当前会话命中某档案镜像根 → 高亮并自动载入编辑表单
	//（用户正在手动编辑时不打扰——以 nameDirty 粗粒度判断）。
	useEffect(() => {
		if (cwd === undefined || typeof cwd !== 'string') return
		const hit = profiles.find((p) => typeof p.mirrorPath === 'string' && (cwd === p.mirrorPath || cwd.startsWith(p.mirrorPath + '/')))
		if (hit === undefined) { setEditingId(''); return }
		setEditingId(hit.id)
		if (!manualMode && !nameDirty && !formTouched) {
			loadIntoFormRef.current(hit)
		}
	}, [cwd, profiles, sshEntries]) // sshEntries 纳入依赖：config 晚于档案列表到达时补一次回现（竞态修复）；nameDirty/manualMode 故意不入 deps
	/** 「＋ 新增」：清空表单进入新增态，脱离 cwd 联动抢占。 */
	const startNew = () => {
		setForm(EMPTY_FORM)
		setNameDirty(false)
		setEditingId('')
		setManualMode(true)
		setFormTouched(false)
		setSshSelected('')
		setMessage(null)
	}

	const set = (key) => (event) => { setFormTouched(true); setForm((prev) => ({ ...prev, [key]: event.target.value })) }

	/** 统一表单载入：档案回填 + 导入下拉回现（host+port 反查 config 别名）。 */
	const loadIntoForm = (profile) => {
		setForm(formOfProfile(profile))
		setEditingId(profile.id)
		setNameDirty(false)
		setManualMode(false)
		setFormTouched(false)
		setMessage(null)
		const hit = sshEntries.find((e) => e.hostName === profile.host && e.port === profile.port)
		setSshSelected(hit !== undefined ? hit.alias : '')
	}
	const loadIntoFormRef = React.useRef(loadIntoForm)
	loadIntoFormRef.current = loadIntoForm

	const importFromSshConfig = (event) => {
		const alias = event.target.value
		if (alias === '') return
		setSshSelected(alias)
		setFormTouched(true)
		const entry = sshEntries.find((e) => e.alias === alias)
		if (entry === undefined) return
		setForm((prev) => ({
			...prev,
			host: entry.hostName,
			port: String(entry.port),
			username: entry.user !== '' ? entry.user : prev.username,
			authMode: entry.identityFile !== '' ? 'key-file' : prev.authMode === 'password' ? 'key-file' : prev.authMode,
			identityFile: entry.identityFile !== '' ? entry.identityFile : prev.identityFile,
			name: prev.name === '' ? entry.alias : prev.name,
		}))
	}

	const run = useCallback((label, action) => {
		setBusy(label)
		setMessage(null)
		Promise.resolve()
			.then(action)
			.finally(() => setBusy(''))
	}, [])

	const { profileBody, password } = payloadOfForm(form)

	const test = () => run('test', () => {
		callJson('/dsh-remote/test', { profile: profileBody, password })
			.then((payload) => {
				if (payload.ok !== true) { setMessage({ ok: false, text: payload.error }); return }
				if (payload.reachable === true) {
					const parts = [`连接成功（${String(payload.latencyMs)}ms）`]
					parts.push(payload.execOk === true ? '执行链路 ✓' : `执行链路 ✗：${String(payload.execError ?? '未知错误')}`)
					if (payload.remotePathExists !== undefined) parts.push(`远端路径${payload.remotePathExists === true ? ' ✓' : ' 不存在'}`)
					parts.push(`rg：${payload.rg === 'system' ? '系统自带' : payload.rg === 'uploaded' ? '已自动上传' : '缺失（glob/grep 不可用）'}`)
					setMessage({ ok: payload.execOk === true, text: parts.join(' · ') })
				} else {
					setMessage({ ok: false, text: `连接失败：${String(payload.message)}` })
				}
			})
			.catch((err) => setMessage({ ok: false, text: err.message }))
	})

	const save = () => run('save', () => {
		callJson('/dsh-remote/save', { profile: profileBody, password })
			.then((payload) => {
				if (payload.ok !== true) { setMessage({ ok: false, text: payload.error }); return }
				if (payload.updatedExisting === true) {
					setMessage({ ok: true, text: `此服务器的该目录已有工作区「${payload.profile.name}」——本次保存更新了原档案，未新建（同一目录只能存在一个）。` })
				} else {
					setMessage({ ok: true, text: `已保存「${payload.profile.name}」并注册侧栏工作区（${String(payload.workspaceTitle)}）。在侧栏该工作区里新建会话即为远程会话。` })
				}
				setForm(EMPTY_FORM)
				setNameDirty(false)
				setEditingId('')
				setManualMode(false)
				setFormTouched(false)
				reload()
			})
			.catch((err) => setMessage({ ok: false, text: err.message }))
	})

	const remove = (profile) => run('remove', () => {
		callJson('/dsh-remote/remove', { id: profile.id })
			.then((payload) => {
				if (payload.ok !== true) { setMessage({ ok: false, text: payload.error }); return }
				setMessage({ ok: true, text: '已删除（侧栏工作区一并移除；其下已有会话变「未分组」，镜像目录保留）' })
				reload()
			})
			.catch((err) => setMessage({ ok: false, text: err.message }))
	})

	// 插件缺席：特性探测失败 → 安装提示，不影响其余标签。
	if (available === false) {
		return h('div', { className: 'dwb-remote-wrap dwb-scroll' },
			h('div', { className: 'dwb-remote-missing' },
				h('span', { className: 'dwb-remote-missingicon' }, remoteIcon()),
				h('div', { className: 'dwb-remote-missingtitle' }, '需要 dsh-remote 插件'),
				h('div', { className: 'dwb-remote-missingsub' },
					'本标签管理 SSH 远程工作区，宿主半未检测到（/dsh-remote 路由不存在）。安装后重启 dsh web：',
				),
				h('code', { className: 'dwb-remote-missingcode' }, 'pnpm dsh plugin --profile web add @bit-ark/dsh-remote'),
			),
		)
	}

	return h('div', { className: 'dwb-remote-wrap' },
		h('div', { className: 'dwb-scroll dwb-remote-body' },
			h('div', { className: 'dwb-remote-cols' },
				// ── 左栏：档案列表（当前会话命中的高亮，点击载入编辑）──
				h('div', { className: 'dwb-remote-listcol' },
					h('div', { className: 'dwb-remote-listhead' },
						h('span', null, '工作区'),
						h('button', { type: 'button', className: 'dwb-remote-mini dwb-remote-minigo', onClick: startNew, title: '新增一台主机/一个项目' }, '＋ 新增'),
					),
					profiles.length === 0
						? h('div', { className: 'dwb-remote-note' }, '还没有档案，在右侧新建。')
						: profiles.map((profile) => h(ProfileRow, {
							key: profile.id,
							profile,
							active: profile.id === editingId,
							busy: busy !== '',
							onEdit: () => { loadIntoForm(profile) },
							onRemove: () => remove(profile),
						})),
				),
				// ── 右栏：表单 ──
				h('div', { className: 'dwb-remote-formcol' },
					h('div', { className: 'dwb-section' }, editingId !== '' ? `编辑：${form.name}` : '新建档案'),
					sshEntries.length > 0 ? h(Field, { label: '从 ~/.ssh/config 导入主机' },
						h('select', { className: 'dwb-remote-input', value: sshSelected, onChange: importFromSshConfig },
							h('option', { value: '' }, '选择主机…'),
							sshEntries.map((entry) => h('option', { key: entry.alias, value: entry.alias },
								`${entry.alias}${entry.hostName !== entry.alias ? ` (${entry.hostName})` : ''}`)),
						),
					) : null,
					h(Field, { label: '名称（留空 = 远端目录名）' },
						h('input', { className: 'dwb-remote-input', value: form.name, placeholder: '例如：生产环境 API', onChange: (event) => { setNameDirty(true); setForm((prev) => ({ ...prev, name: event.target.value })) } }),
					),
					h('div', { className: 'dwb-remote-row' },
						h(Field, { label: '主机' },
							h('input', { className: 'dwb-remote-input', value: form.host, placeholder: 'host 或 IP', onChange: set('host') }),
						),
						h(Field, { label: '端口' },
							h('input', { className: 'dwb-remote-input dwb-remote-port', value: form.port, inputMode: 'numeric', onChange: set('port') }),
						),
					),
					h(Field, { label: '用户名' },
						h('input', { className: 'dwb-remote-input', value: form.username, placeholder: 'ssh 用户', onChange: set('username') }),
					),
					h(Field, { label: '登录方式' },
						h('select', { className: 'dwb-remote-input', value: form.authMode, onChange: set('authMode') },
							h('option', { value: 'key-file' }, '密钥文件'),
							h('option', { value: 'agent' }, 'ssh-agent（ssh-add 过的密钥）'),
							h('option', { value: 'password' }, '密码'),
						),
					),
					form.authMode === 'key-file' ? h(Field, { label: '私钥文件' },
						h('div', { className: 'dwb-remote-pickrow' },
							h('input', { className: 'dwb-remote-input dwb-remote-pickpath', value: form.identityFile, placeholder: '点右侧「浏览」从目录树选择', readOnly: true, onChange: () => {} }),
							h('button', { type: 'button', className: 'dwb-remote-mini dwb-remote-minigo', onClick: () => setLocalPickerOpen(true) }, '浏览'),
						),
					) : null,
					form.authMode === 'password' ? h(Field, { label: '密码（留空 = 沿用已保存）' },
						h('input', { className: 'dwb-remote-input', type: 'password', value: form.password, autoComplete: 'new-password', onChange: set('password') }),
					) : null,
					h(Field, { label: '远端项目目录' },
						h('div', { className: 'dwb-remote-pickrow' },
							h('input', { className: 'dwb-remote-input dwb-remote-pickpath', value: form.remotePath, placeholder: '填好连接信息后点「浏览」选择', readOnly: true, onChange: () => {} }),
							h('button', { type: 'button', className: 'dwb-remote-mini dwb-remote-minigo', onClick: () => setRemotePickerOpen(true) }, '浏览'),
						),
					),
					h('div', { className: 'dwb-remote-ops' },
						h('button', { type: 'button', className: 'dwb-remote-btn', disabled: busy !== '', onClick: test }, '测试连接'),
						h('button', { type: 'button', className: 'dwb-remote-btn dwb-remote-primary', disabled: busy !== '', onClick: save }, '保存并加入侧栏'),
					),
					message !== null ? h('div', { className: 'dwb-remote-message' + (message.ok ? '' : ' dwb-remote-err') }, message.text) : null,
				),
			),
		),
		localPickerOpen ? h(LocalFileBrowser, {
			onPick: (path) => setForm((prev) => ({ ...prev, identityFile: path })),
			onClose: () => setLocalPickerOpen(false),
		}) : null,
		remotePickerOpen ? h(RemoteDirBrowser, {
			profile: profileBody,
			password,
			// 名称留空且用户未手填 → 自动取目录名（保存兜底之外的前置预填）；
			// 同机同目录预检：命中已有档案时就地预告（同一目录只能存在一个）。
			onPick: (path) => {
				setForm((prev) => ({
					...prev,
					remotePath: path,
					name: prev.name === '' ? baseNameOf(path) : prev.name,
				}))
				const dup = profiles.find((p) => p.host === form.host && Number(p.port) === Number(form.port) && p.remotePath === path)
				if (dup !== undefined) {
					setMessage({ ok: false, text: `该目录已是工作区「${dup.name}」——同一目录只能存在一个，保存将只更新原档案。` })
				} else {
					setMessage(null)
				}
			},
			onClose: () => setRemotePickerOpen(false),
		}) : null,
	)
}
